import { RosterChangesPanel } from "@/payroll/roster/roster-changes-panel";
import { RosterEditDrawer } from "@/payroll/roster/roster-edit-drawer";
import type {
  PayrollTeam,
  PayrollRosterContract,
  PayrollRosterDraftMember as DraftMember,
  PayrollRosterEditTarget as EditTarget,
  PayrollRosterField,
  PayrollRosterMember,
} from "@/payroll/roster/types";
import {
  createPayrollRosterMember,
  type MemberChange,
} from "@/payroll/roster/utils";

interface RosterEditorSidePanelProps {
  businessId: string;
  teams: PayrollTeam[];
  fields: PayrollRosterField[];
  fieldOptions: Record<string, string[]>;
  contract: PayrollRosterContract;
  editTarget: EditTarget;
  draftMembers: DraftMember[];
  suggestionDrafts: DraftMember[];
  reviewOpen: boolean;
  changes: MemberChange[];
  isSavingDraft: boolean;
  isDismissingSuggestion: boolean;
  publishing: boolean;
  onSave: (member: PayrollRosterMember) => Promise<void>;
  onCancel: () => void;
  onRemove: () => void;
  onDismissSuggestion: (draft: DraftMember | undefined) => void;
  onCloseReview: () => void;
  onPublish: () => void;
}

export const RosterEditorSidePanel = ({
  businessId,
  teams,
  fields,
  fieldOptions,
  contract,
  editTarget,
  draftMembers,
  suggestionDrafts,
  reviewOpen,
  changes,
  isSavingDraft,
  isDismissingSuggestion,
  publishing,
  onSave,
  onCancel,
  onRemove,
  onDismissSuggestion,
  onCloseReview,
  onPublish,
}: RosterEditorSidePanelProps) => {
  const editingDraft =
    editTarget?.mode === "edit"
      ? draftMembers.find((draft) => draft.id === editTarget.draftId)
      : undefined;
  const editingSuggestion =
    editTarget?.mode === "suggestion"
      ? suggestionDrafts.find(
          (draft) => draft.suggestion?.user_id === editTarget.userId,
        )
      : undefined;
  const drawerInitialMember =
    editTarget?.mode === "new"
      ? createPayrollRosterMember(fields)
      : editTarget?.mode === "suggestion"
        ? editingSuggestion?.member
        : editingDraft?.member;
  const drawerKey =
    editTarget?.mode === "new"
      ? "new"
      : editTarget?.mode === "suggestion"
        ? `suggestion-${editTarget.userId}`
        : editTarget?.draftId;

  return editTarget != null && drawerInitialMember ? (
    <RosterEditDrawer
      key={drawerKey}
      businessId={businessId}
      teams={teams}
      title={
        editTarget.mode === "edit" ? "Edit person" : "Add person to roster"
      }
      fields={fields}
      fieldOptions={fieldOptions}
      contract={contract}
      initialMember={drawerInitialMember}
      canRemove={editTarget.mode === "edit"}
      onDismiss={
        editTarget.mode === "suggestion"
          ? () => onDismissSuggestion(editingSuggestion)
          : undefined
      }
      showInitialErrors={editTarget.mode === "edit"}
      saving={isSavingDraft || isDismissingSuggestion}
      dismissing={isDismissingSuggestion}
      onSave={onSave}
      onCancel={onCancel}
      onRemove={onRemove}
    />
  ) : reviewOpen ? (
    <RosterChangesPanel
      changes={changes}
      publishing={publishing}
      onClose={onCloseReview}
      onPublish={onPublish}
    />
  ) : null;
};
